<script setup lang="ts">
import { computed, onBeforeMount, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { BaseIcon, BaseLoader } from '@magma-app/magma-lapilli';

import type { PropType } from 'vue';

import HelperCampaignDescription from '@/views/helper-signup/components/HelperCampaignDescription.vue';
import HelperCampaignHeader from '@/views/helper-signup/components/HelperCampaignHeader.vue';
import HelperCampaignMeta from '@/views/helper-signup/components/HelperCampaignMeta.vue';
import HelperCriteriaForm from '@/views/helper-signup/components/HelperCriteriaForm.vue';
import HelperProfileForm from '@/views/helper-signup/components/HelperProfileForm.vue';
import HelperSignUpFooter from '@/views/helper-signup/components/HelperSignUpFooter.vue';
import { useHelperCampaignViewModel } from '@/views/helper-signup/composables/useHelperCampaignViewModel';
import { useHelperSignupCriteria } from '@/views/helper-signup/composables/useHelperSignupCriteria';
import { useHelperSignupFlow } from '@/views/helper-signup/composables/useHelperSignupFlow';
import { useHelperSignupProfile } from '@/views/helper-signup/composables/useHelperSignupProfile';
import { useHelperSignupValidation } from '@/views/helper-signup/composables/useHelperSignupValidation';
import type { HelperCampaignToJoin } from '@/views/helper-signup/types';

import { useCommonStore } from '@/stores/common';
import { useUserStore } from '@/stores/user';

const commonStore = useCommonStore();
const userStore = useUserStore();

const { t, locale } = useI18n({ useScope: 'global' });

const props = defineProps({
  campaignToJoin: {
    type: Object as PropType<HelperCampaignToJoin | null>,
    default: undefined,
  },
});

const emit = defineEmits<{
  (event: 'close'): void;
  (event: 'notify'): void;
}>();

const initialized = ref(false);

const showBack = computed(() => Boolean(props.campaignToJoin));

const formData = reactive({
  profilePicture: null as File | null,
  phoneNumber: userStore.user?.phoneNumber || '',
});

const { campaign, campaignCriteria } = useHelperCampaignViewModel({
  campaignToJoin: props.campaignToJoin,
});

const { v$ } = useHelperSignupValidation({
  formData,
  t,
});

const { changePhoneNumber, uploadProfilePicture } = useHelperSignupProfile({
  formData,
  v$,
  userStore,
});

const {criteriaAnswers,
  hasStrongCriteriaSelected,
  getCriteriaOptions,
  buildUserCriteriaPayload} = useHelperSignupCriteria({
  campaignCriteria,
  userCriteria: userStore.user?.userCriteria,
});

const {
  consentSent,
  formSectionRef,
  primaryButtonText,
  isPrimaryDisabled,
  onPrimaryAction,
} = useHelperSignupFlow({
  campaign,
  hasStrongCriteriaSelected,
  buildUserCriteriaPayload,
  commonStore,
  userStore,
  t,
  emit,
});

onBeforeMount(() => {
  if (props.campaignToJoin && userStore.user?.language) {
    locale.value = userStore.user.language;
  } else {
    locale.value = navigator.language.split('-')[0] === 'fr' ? 'fr' : 'en';
  }

  initialized.value = true;
});

const onCloseSignUp = () => {
  emit('close');
};
</script>

<template>
  <div v-if="initialized" class="relative" data-cy="moments-summary">
    <HelperCampaignHeader
      :header-url="campaign.headerUrl"
      :show-back="showBack"
      @back="onCloseSignUp"
    />

    <div
      :class="[
        'mx-auto mt-8 max-w-4xl px-4 text-sm text-foreground-emphasis',
        showBack ? 'lg:px-0' : 'lg:px-8'
      ]"
    >
      <div
        v-if="showBack"
        class="hidden cursor-pointer items-center lg:flex"
        @click="onCloseSignUp"
      >
        <BaseIcon
          class="mr-2 rounded-full bg-background-subtle p-1.5"
          color="#5F6C85"
          icon="arrow-left"
        />

        <span class="text-mgm-txt-sm font-medium text-foreground-default">
          {{ $t('helperCampaignSignUpView.back') }}
        </span>
      </div>

      <h1
        :class="[
          'text-mgm-txt-lg font-medium text-foreground-emphasis',
          { 'lg:mt-4': showBack }
        ]"
        data-cy="campaign-title"
      >
        {{ campaign.name }}
      </h1>

      <HelperCampaignMeta
        :campaign-admin="campaign.campaignAdmin"
        :helpers-profile-picture="campaign.helpersProfilePicture"
        :helpers-count="campaign.helpersCount"
        :points="campaign.points"
        :show-back="showBack"
      />

      <HelperCampaignDescription :html="campaign.description" />

      <!-- TO CHANGE (26/08/2025) -->
      <!-- <h2 class="mt-4 text-mgm-txt-md font-medium text-foreground-emphasis">
        Moments
      </h2> -->

      <!-- <p class="mt-1 text-mgm-txt-sm font-normal text-foreground-default">
        {{ $t("helperCampaignSignUpView.momentsDescription") }}
      </p> -->

      <!-- <div
        class="mt-4 rounded-[5px] border border-border-subtle p-4"
        data-cy="moment"
      >

        <div class="mt-4 flex items-center">
          <BaseBadge
            v-if="campaignData.moment?.points"
            class="mr-2"
            :text="campaignData.moment.points"
            size="md"
            icon-type="icon"
            icon-name="star"
          />
        </div>
      </div> -->

      <div v-if="consentSent" ref="formSectionRef" data-cy="form">
        <HelperProfileForm
          :form-data="formData"
          :validation="v$"
          :locale="$i18n.locale"
          :preview-url="userStore.user?.profilePictureUrl || undefined"
          @phone-change="changePhoneNumber"
          @picture-change="uploadProfilePicture"
        />

        <HelperCriteriaForm
          :criteria="campaignCriteria"
          :answers="criteriaAnswers"
          :get-options="getCriteriaOptions"
        />
      </div>
    </div>

    <HelperSignUpFooter
      :button-text="primaryButtonText"
      :disabled="isPrimaryDisabled"
      :show-terms="!consentSent"
      @primary="onPrimaryAction"
    />
  </div>

  <BaseLoader v-else class="min-h-screen" />
</template>

<style>
.description h1,
.description h2,
.description h3,
.description h4,
.description h5,
.description h6 {
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
  color: #1c1f27;
}

.description p,
.description span,
.description a {
  color: #5f6c85 !important;
}

.description p:first-child,
.description img:first-child {
  margin-top: 0px !important;
}
</style>
